import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollableElement,
  CdkVirtualScrollViewport,
} from "@angular/cdk/scrolling";
import { Component, OnDestroy } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { ORDER_TYPE_CONFIG } from "src/app/configs/order-type-config";
import { OrderSortOption } from "src/app/enums/order-sort-option";
import { OrderType } from "src/app/enums/order-type";
import { OrderDto } from "src/app/models/order-dto";
import { OrderService } from "src/app/services/order.service";
import { OrderRowComponent } from "../../components/orders/order-row/order-row.component";
import { LazyLoaderDirective } from "../../directives/lazy-loader.directive";

@Component({
  selector: "oaibox-orders-page",
  templateUrl: "./orders-page.component.html",
  styleUrl: "./orders-page.component.css",
  imports: [
    CdkVirtualScrollableElement,
    ReactiveFormsModule,
    FormsModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    OrderRowComponent,
    LazyLoaderDirective,
  ],
})
export class OrdersPageComponent implements OnDestroy {
  protected readonly ORDER_TYPE_CONFIG = ORDER_TYPE_CONFIG;

  protected readonly AVAILABLE_ORDER_STATES = Object.values(OrderType);
  private readonly ORDER_STATE_FILTER_LOCAL_STORAGE_ITEM_KEY =
    "84f67281-2b58-4324-bd6a-ed8d4c3dbad5";
  protected orderStateOption?: OrderType;

  protected readonly AVAILABLE_SORT_OPTIONS = Object.values(OrderSortOption);
  private readonly SORT_OPTION_LOCAL_STORAGE_ITEM_KEY =
    "6bcb2bf8-89ef-4213-a180-21db812b0edb";
  protected sortOption: OrderSortOption = OrderSortOption.ORDER_DATE_CLOSEST;

  private readonly orders: Map<string, OrderDto> = new Map();
  protected shownOrders: OrderDto[] = [];

  protected keyword: string = "";

  protected isLoading = false;
  private readonly initialIncrement = 10;
  private dataLength = this.initialIncrement;

  private readonly ordersSubscription?: Subscription;

  constructor(orderService: OrderService) {
    const orderStateOption = window.localStorage.getItem(
      this.ORDER_STATE_FILTER_LOCAL_STORAGE_ITEM_KEY
    );
    this.orderStateOption = (orderStateOption as OrderType) || undefined;

    const sortOption = window.localStorage.getItem(
      this.SORT_OPTION_LOCAL_STORAGE_ITEM_KEY
    );
    this.sortOption =
      (sortOption as OrderSortOption) || OrderSortOption.ORDER_DATE_CLOSEST;

    this.ordersSubscription = orderService.getOrders$().subscribe((orders) => {
      this.orders.clear();
      orders.forEach((order) => this.orders.set(order.id, order));
      this.filterMatchingOrders();
    });
    orderService.refreshOrders();
  }

  protected filterMatchingOrders(): void {
    let filteredOrders = [...this.orders.values()];

    // Filter orders by state
    if (this.orderStateOption) {
      filteredOrders = filteredOrders.filter(
        (order) => order.orderType === this.orderStateOption
      );
    }
    this.shownOrders = filteredOrders;

    // Filter by keywords
    if (this.keyword.trim() !== "") this.filterOrders();

    this.sortOrders();
  }

  private filterOrders(): void {
    const filterFn = (order: OrderDto) => {
      const keyword = this.keyword.trim().toLowerCase();
      return (
        order.title?.toLowerCase().includes(keyword) ||
        order.customerName?.toLowerCase().includes(keyword) ||
        order.customerEmailAddress?.toLowerCase().includes(keyword)
      );
    };
    this.shownOrders = this.shownOrders.filter(filterFn);
  }

  private sortOrders(): void {
    this.shownOrders.sort(this.getSortFn());
  }

  private getSortFn(): (order1: OrderDto, order2: OrderDto) => number {
    switch (this.sortOption) {
      case OrderSortOption.ORDER_DATE_FURTHEST:
        return (order1, order2) =>
          this.compareDates(order1.startDate, order2.startDate, true);
      case OrderSortOption.DUE_DATE_CLOSEST:
        return (order1, order2) =>
          this.compareDates(order1.dueDate, order2.dueDate, true);
      case OrderSortOption.DUE_DATE_FURTHEST:
        return (order1, order2) =>
          this.compareDates(order1.dueDate, order2.dueDate, false);
      default:
        return (order1, order2) =>
          this.compareDates(order1.startDate, order2.startDate, false);
    }
  }

  // Orders without the relevant date always sort to the end, in either direction
  private compareDates(
    date1: string | undefined,
    date2: string | undefined,
    ascending: boolean
  ): number {
    if (!date1 && !date2) return 0;
    if (!date1) return 1;
    if (!date2) return -1;
    const timestamp1 = new Date(date1).getTime();
    const timestamp2 = new Date(date2).getTime();
    return ascending ? timestamp1 - timestamp2 : timestamp2 - timestamp1;
  }

  protected updateOrderStateOption(state: OrderType | undefined): void {
    this.orderStateOption = state;
    if (state) {
      window.localStorage.setItem(
        this.ORDER_STATE_FILTER_LOCAL_STORAGE_ITEM_KEY,
        state
      );
    } else {
      window.localStorage.removeItem(
        this.ORDER_STATE_FILTER_LOCAL_STORAGE_ITEM_KEY
      );
    }

    this.filterMatchingOrders();
  }

  protected updateSortOption(option: OrderSortOption): void {
    this.sortOption = option;
    window.localStorage.setItem(
      this.SORT_OPTION_LOCAL_STORAGE_ITEM_KEY,
      option
    );

    this.filterMatchingOrders();
  }

  protected loadMoreData(): void {
    this.isLoading = true;
    this.dataLength += this.initialIncrement;
    this.filterMatchingOrders();
    setTimeout(() => (this.isLoading = false), 1000);
  }

  ngOnDestroy(): void {
    this.ordersSubscription?.unsubscribe();
  }
}
